import { useCallback } from "react"

import { Box, SvgIcon as MUISvgIcon, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import type { FC, ResponsiveImageType } from ".."
import { Accordion, Container, Grid, IconButton, ResponsiveImage, SvgIcon, Tag, WYSIWYG } from ".."

type StaffTag = {
  title: string
  icon?: string
  slug?: string
}

type StaffProfileOverviewType = {
  title: string
  description: string
  image: ResponsiveImageType
  tags: StaffTag[]
  items: any
}

const StaffProfileOverview: FC<StaffProfileOverviewType> = ({
  title,
  description,
  tags,
  image,
  items,
}) => {
  return (
    <Box position="relative">
      <BackgroundIcon />
      <Container
        position="relative"
        sx={{
          px: { xs: 10 },
          mb: { xs: 15, lg: 55 },
        }}
      >
        <Grid rowGap={0}>
          <Box
            position={{ xs: "relative", md: "static" }}
            gridColumn={{ xs: "1 / -1", md: "1 / span 3", lg: "1 / span 4" }}
          >
            <Box
              position={{ xs: "relative", md: "sticky" }}
              sx={(theme) => ({
                marginTop: { xs: "max(-82.12%, -349px)", md: "-150px" },
                maxWidth: { lg: 450 },
                width: "100%",
                aspectRatio: { xs: "366 / 425", lg: "450 / 523" },
                borderRadius: theme.spacing(1.5),
                top: { md: 140 },
                zIndex: 1,
              })}
            >
              <StaffProfilePicture image={image} />
            </Box>
            <StaffProfileButton />
          </Box>

          <Box
            gridColumn={{ xs: "1 / -1", md: "2 / -2", lg: "6 / -2" }}
            display="flex"
            flexDirection="column"
            mt={{ xs: 0, md: 35 }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent={{ xs: "center", md: "flex-start" }}
              gap={2}
              py={{ xs: 8, md: 0 }}
              sx={(theme) => ({
                borderBottom: "none",
                [theme.breakpoints.down("md")]: {
                  borderBottom: `1px solid ${alpha(theme.palette["secondary.800"].main, 0.1)}`,
                },
              })}
            >
              {tags?.map((tag, i) => (
                <Tag
                  key={i}
                  label={tag.title}
                  uri={tag.slug}
                  icon={tag.icon}
                  color="white"
                  hasBorder
                />
              ))}
            </Box>
            <StaffProfileTitle title={title} />
            <StaffProfileDescription description={description} />
            <StaffProfileFacts items={items} />
          </Box>
        </Grid>
      </Container>
    </Box>
  )
}

export default StaffProfileOverview

/** A sub-component for the Staff profile picture */
const StaffProfilePicture: FC<Pick<StaffProfileOverviewType, "image">> = ({ image }) => {
  return (
    <Box
      sx={{
        background: "#F2EAD3",
        borderRadius: 1.5,
        overflow: "hidden",
        width: "100%",
        height: "100%",
      }}
    >
      <ResponsiveImage image={image} />
    </Box>
  )
}

/** A sub-component for the Staff profile title */
const StaffProfileTitle: FC<Pick<StaffProfileOverviewType, "title">> = ({ title }) => {
  return (
    <Box mt={{ xs: 12, md: 15 }} mb={{ xs: 10, md: 15 }}>
      <div id="profile-overview">
        <Typography variant="heading.3xl" component="h1">
          {title}
        </Typography>
      </div>
    </Box>
  )
}

/** A sub-component for the Staff profile description */
const StaffProfileDescription: FC<Pick<StaffProfileOverviewType, "description">> = ({
  description,
}) => {
  return <WYSIWYG content={description} />
}

/** A sub-component for the Staff profile button */
const StaffProfileButton: FC = () => {
  const handleScroll = useCallback(() => {
    if (typeof window !== "undefined") {
      const element = document?.getElementById("profile-overview")
      if (element) element.scrollIntoView({ behavior: "smooth" })
    }
  }, [])

  return (
    <Box
      sx={(theme) => ({
        "display": "flex",
        "position": "absolute",
        "top": -32,
        "right": 20,
        "transform": "rotate(-90deg)",
        "borderRadius": "100px",
        "background": theme.palette["primary.800"].main,
        "zIndex": 2,
        "&:hover": { cursor: "pointer" },
        [theme.breakpoints.down("md")]: {
          top: "auto",
          bottom: 10,
          right: 10,
        },
      })}
    >
      <IconButton
        variant="quaternary"
        icon={
          <SvgIcon
            variant="arrow"
            size={20}
            sx={{
              fill: (theme) => theme.palette["neutral.100"].main,
            }}
          />
        }
        onClick={handleScroll}
      />
    </Box>
  )
}

/** A sub-component for the Staff profile facts */
const StaffProfileFacts: FC<Pick<StaffProfileOverviewType, "items">> = ({ items }) => {
  if (!items || items.length === 0) return null

  return (
    <Box mt={{ xs: 12, md: 35 }}>
      <Accordion items={items} />
    </Box>
  )
}

/** A sub-component for the animated svg in the bg  */
const BackgroundIcon = () => {
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: 0,
        display: { xs: "none", md: "block" },
        overflow: "hidden",

        [theme.breakpoints.down(1600)]: {
          right: 920,
        },

        [theme.breakpoints.up(1600)]: {
          left: 0,
        },
      })}
    >
      <MUISvgIcon
        viewBox="0 0 663 418"
        sx={{
          width: 663,
          height: 418,
          fill: "none",
        }}
      >
        <path
          d="M-1.62173e-07 413.252C84.125 448.099 77.6391 226 198.5 226C385.897 226 347.5 413.252 503.935 375.244C652.944 339.04 506.583 -9.57436 662 1.2402"
          stroke="#F05B2A"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="0.1 10"
        />
      </MUISvgIcon>
    </Box>
  )
}
